import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import { Phone, MessageCircle, Plus, Trash2, Pencil, Hammer } from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";

/** Prepend +91 country code for Indian numbers that don't already have it. */
function formatWhatsApp(phone) {
  const digits = (phone || "").replace(/[^0-9]/g, "");
  if (digits.startsWith("91") && digits.length >= 12) return digits;
  if (digits.length === 10) return `91${digits}`;
  return digits;
}

function KarigarForm({ initial, onSubmit, submitLabel = "Save" }) {
  const [form, setForm] = useState(
    initial || { name: "", phone: "", specialization: "", labour_rate_per_gram: 0, labour_rate_per_piece: 0, notes: "" }
  );

  return (
    <form
      onSubmit={(e) => { e.preventDefault(); onSubmit(form); }}
      className="space-y-4"
      data-testid="karigar-form"
    >
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Name *</Label>
          <Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="karigar-name-input" />
        </div>
        <div className="space-y-2">
          <Label>Phone *</Label>
          <Input required value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="karigar-phone-input" />
        </div>
        <div className="space-y-2 sm:col-span-2">
          <Label>Specialization</Label>
          <Input placeholder="e.g. Antique necklaces, kundan, casting" value={form.specialization} onChange={(e) => setForm({ ...form, specialization: e.target.value })} />
        </div>
        <div className="space-y-2">
          <Label>Labour ₹ / gram</Label>
          <Input type="number" step="0.01" value={form.labour_rate_per_gram} onChange={(e) => setForm({ ...form, labour_rate_per_gram: parseFloat(e.target.value) || 0 })} />
        </div>
        <div className="space-y-2">
          <Label>Labour ₹ / piece</Label>
          <Input type="number" step="0.01" value={form.labour_rate_per_piece} onChange={(e) => setForm({ ...form, labour_rate_per_piece: parseFloat(e.target.value) || 0 })} />
        </div>
      </div>
      <div className="space-y-2">
        <Label>Notes</Label>
        <Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
      </div>
      <DialogFooter>
        <Button type="submit" className="bg-zinc-900 hover:bg-zinc-800" data-testid="karigar-form-submit">{submitLabel}</Button>
      </DialogFooter>
    </form>
  );
}

export default function KarigarsList() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  const load = async () => {
    const { data } = await api.get("/karigars");
    setItems(data);
  };
  useEffect(() => { load(); }, []);

  const onCreate = async (form) => {
    try {
      await api.post("/karigars", form);
      toast.success("Karigar added");
      setOpen(false);
      load();
    } catch { toast.error("Could not add"); }
  };

  const onUpdate = async (form) => {
    try {
      await api.put(`/karigars/${editTarget.id}`, form);
      toast.success("Updated");
      setEditTarget(null);
      load();
    } catch { toast.error("Could not update"); }
  };

  const onDelete = async (id) => {
    try {
      await api.delete(`/karigars/${id}`);
      load();
      toast.success("Deleted");
    } catch (e) { toast.error(e.response?.data?.detail || "Could not delete"); } finally {
      setDeleteConfirm(null);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="overline text-zinc-500">Craftsmen</div>
          <h1 className="font-heading font-black text-4xl sm:text-5xl tracking-tighter mt-1">Karigars</h1>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="bg-zinc-900 hover:bg-zinc-800" data-testid="add-karigar-button">
              <Plus className="w-4 h-4 mr-1.5" /> Add Karigar
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-2xl">
            <DialogHeader><DialogTitle>New Karigar</DialogTitle></DialogHeader>
            <KarigarForm onSubmit={onCreate} submitLabel="Add Karigar" />
          </DialogContent>
        </Dialog>
      </div>

      {items.length === 0 ? (
        <Card className="p-12 text-center text-zinc-500 text-sm">No karigars yet.</Card>
      ) : (
        <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="karigar-list">
          {items.map((k) => (
            <Card key={k.id} className="p-5">
              <div className="flex items-start gap-3">
                <div className="w-11 h-11 rounded-md bg-zinc-900 grid place-items-center text-white">
                  <Hammer className="w-4 h-4" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-heading font-bold text-lg leading-none">{k.name}</div>
                  <div className="text-xs text-zinc-500 mt-1 truncate">{k.specialization || "—"}</div>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3 mt-4 text-sm">
                <div>
                  <div className="overline text-zinc-500">Active</div>
                  <div className="font-bold num text-2xl">{k.active_orders}</div>
                </div>
                <div>
                  <div className="overline text-zinc-500">Done</div>
                  <div className="font-bold num text-2xl">{k.completed_orders}</div>
                </div>
              </div>

              <div className="text-xs text-zinc-500 mt-4 space-y-0.5">
                <div>Labour: ₹{k.labour_rate_per_gram}/g · ₹{k.labour_rate_per_piece}/pc</div>
                <div className="num">{k.phone}</div>
              </div>

              <div className="flex items-center gap-1 mt-4 pt-3 border-t border-border">
                <a href={`tel:${k.phone}`}><Button size="sm" variant="outline" className="h-8"><Phone className="w-3 h-3 mr-1" /> Call</Button></a>
                <a href={`https://wa.me/${formatWhatsApp(k.phone)}`} target="_blank" rel="noreferrer">
                  <Button size="sm" variant="outline" className="h-8 text-emerald-600"><MessageCircle className="w-3 h-3 mr-1" /> WA</Button>
                </a>
                <div className="flex-1" />
                <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => setEditTarget(k)}>
                  <Pencil className="w-3.5 h-3.5" />
                </Button>
                <Button size="icon" variant="ghost" className="h-8 w-8 text-red-600" onClick={() => setDeleteConfirm(k)}>
                  <Trash2 className="w-3.5 h-3.5" />
                </Button>
              </div>
            </Card>
          ))}
        </div>
      )}

      <AlertDialog open={!!deleteConfirm} onOpenChange={(o) => !o && setDeleteConfirm(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Karigar?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete{" "}
              <span className="font-semibold">{deleteConfirm?.name}</span>? This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-red-600 hover:bg-red-700 text-white"
              onClick={() => onDelete(deleteConfirm?.id)}
            >
              Delete Karigar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog open={!!editTarget} onOpenChange={(o) => !o && setEditTarget(null)}>
        <DialogContent className="sm:max-w-2xl">
          <DialogHeader><DialogTitle>Edit Karigar</DialogTitle></DialogHeader>
          {editTarget && <KarigarForm initial={editTarget} onSubmit={onUpdate} submitLabel="Save Changes" />}
        </DialogContent>
      </Dialog>
    </div>
  );
}
